import type { paths } from "@src/packages/openapi/schema";

type PhaseResponse =
  paths["/{tenantId}/cycles/{id}/phases"]["get"]["responses"][200]["content"]["application/json"];

export type ApiFertilityMetric = NonNullable<
  PhaseResponse["data"][number]["fertilityMetrics"]
>[number];

type FertilityMetricType = Exclude<ApiFertilityMetric["type"], null>;

export const STEP_FIELD_TO_METRIC: Record<string, FertilityMetricType> = {
  eggsCollected: "retrieved_oocytes",
  matureEggs: "m2_count",
  eggsFrozen: "frozen_embryo",
  eggsInjected: "injected_oocytes",
  eggsFertilized: "fertilized_oocytes",
  embryosTransferred: "transferred_embryo",
  embryosFrozen: "frozen_embryo",
  embryosDay3: "embryos_day_3",
  embryosDay5: "embryos_day_5",
};

type PhaseWithMetrics = {
  fertilityMetrics?: ApiFertilityMetric[];
};

/**
 * Serializes treatment counts without collapsing a clinical zero into null.
 * Missing values stay absent so the API can distinguish unknown from zero.
 */
export function buildFertilityMetrics(
  data: Record<string, string | number | boolean | null>,
  existingPhase?: PhaseWithMetrics | null,
):
  | { id?: string; type: FertilityMetricType; value: number | null }[]
  | undefined {
  const metrics: {
    id?: string;
    type: FertilityMetricType;
    value: number | null;
  }[] = [];
  const seenTypes = new Map<FertilityMetricType, number>();

  for (const [field, metricKey] of Object.entries(STEP_FIELD_TO_METRIC)) {
    const val = data[field];
    if (
      val === undefined ||
      val === null ||
      (typeof val === "string" && val.trim() === "")
    ) {
      continue;
    }
    const numericValue = typeof val === "number" ? val : Number(val);
    if (!Number.isFinite(numericValue)) continue;

    const existingIndex = seenTypes.get(metricKey);
    if (existingIndex !== undefined) {
      metrics[existingIndex]!.value = numericValue;
      continue;
    }

    seenTypes.set(metricKey, metrics.length);
    const metric: {
      id?: string;
      type: FertilityMetricType;
      value: number | null;
    } = {
      type: metricKey,
      value: numericValue,
    };
    const existing = existingPhase?.fertilityMetrics?.find(
      (candidate) => candidate.type === metricKey,
    );
    if (existing?.id) metric.id = existing.id;
    metrics.push(metric);
  }

  return metrics.length > 0 ? metrics : undefined;
}
